import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';
import { 
  Receipt, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  Copy,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Hash,
  Calendar,
  User,
  ArrowRight,
  Plus,
  Minus
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { copyToClipboard } from '@vaiu/utils';

interface AppliedToken {
  tokenCode: string;
  amount: number;
  currency: string;
  appliedAt: string;
  appliedBy?: string;
  tokenCreatedAt?: string;
  tokenCreatedBy?: string;
  status: 'applied' | 'pending' | 'failed';
  transactionId?: string;
}

interface InvoiceTokenBreakdownProps {
  invoiceId: string;
  invoiceAmount: number;
  invoiceCurrency: string;
  invoiceDescription?: string;
  invoiceCreatedBy?: string;
  invoiceCreatedAt?: string;
  appliedTokens: AppliedToken[];
  totalPaid: number;
  remainingBalance: number;
  status: 'pending' | 'partial' | 'completed' | 'overpaid';
  className?: string;
}

const InvoiceTokenBreakdown: React.FC<InvoiceTokenBreakdownProps> = ({
  invoiceId,
  invoiceAmount,
  invoiceCurrency,
  invoiceDescription,
  invoiceCreatedBy,
  invoiceCreatedAt,
  appliedTokens,
  totalPaid,
  remainingBalance,
  status,
  className = ''
}) => {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAllTokens, setShowAllTokens] = useState(false);

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency + ' ';
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'partial': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overpaid': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'partial': return <Clock className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'overpaid': return <Plus className="w-4 h-4" />;
      default: return <Receipt className="w-4 h-4" />;
    }
  };

  const handleCopyToken = async (tokenCode: string) => {
    await copyToClipboard(tokenCode);
    toast({
      title: "Token code copied!",
      description: `Token ${tokenCode} copied to clipboard`,
    });
  };

  const handleCopyInvoice = async () => {
    await copyToClipboard(invoiceId);
    toast({
      title: "Invoice ID copied!",
      description: `Invoice ${invoiceId} copied to clipboard`,
    });
  };

  // Sort tokens by application time (most recent first)
  const sortedTokens = [...appliedTokens].sort((a, b) => 
    new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
  );

  // Show first 3 tokens initially, with option to show all
  const tokensToShow = showAllTokens ? sortedTokens : sortedTokens.slice(0, 3);
  const hasMoreTokens = sortedTokens.length > 3;

  return (
    <Card className={`${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Receipt className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Invoice Payment Breakdown</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
                <Badge className={getStatusColor(status)}>
                  {getStatusIcon(status)}
                  <span className="ml-1 capitalize">{status}</span>
                </Badge>
                <span className="text-sm text-gray-500">
                  {appliedTokens.length} token{appliedTokens.length !== 1 ? 's' : ''} applied
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Invoice Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900">Invoice Details</h4>
                <div className="flex items-center space-x-2 mt-1">
                  <Hash className="w-3 h-3 text-gray-400" />
                  <span className="text-sm text-gray-600 font-mono">{invoiceId}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyInvoice}
                    className="h-6 w-6 p-0"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {getCurrencySymbol(invoiceCurrency)}{invoiceAmount.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600">{invoiceCurrency}</div>
              </div>
            </div>

            {invoiceDescription && (
              <p className="text-sm text-gray-700 mb-2">{invoiceDescription}</p>
            )}

            {invoiceCreatedBy && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="w-3 h-3" />
                <span>Created by @{invoiceCreatedBy}</span>
                {invoiceCreatedAt && (
                  <>
                    <span>•</span>
                    <Calendar className="w-3 h-3" />
                    <span>{formatDateTime(invoiceCreatedAt)}</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Payment Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Payment Progress</span>
              <span className="font-medium">
                {getCurrencySymbol(invoiceCurrency)}{totalPaid.toFixed(2)} of {getCurrencySymbol(invoiceCurrency)}{invoiceAmount.toFixed(2)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  status === 'completed' ? 'bg-green-500' :
                  status === 'partial' ? 'bg-yellow-500' :
                  status === 'overpaid' ? 'bg-purple-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min((totalPaid / invoiceAmount) * 100, 100)}%` }}
              />
            </div>
            {remainingBalance > 0 && (
              <div className="text-sm text-amber-600">
                Remaining: {getCurrencySymbol(invoiceCurrency)}{remainingBalance.toFixed(2)}
              </div>
            )}
            {remainingBalance < 0 && (
              <div className="text-sm text-purple-600">
                Overpaid: {getCurrencySymbol(invoiceCurrency)}{Math.abs(remainingBalance).toFixed(2)}
              </div>
            )}
          </div>

          <Separator />

          {/* Applied Tokens List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">Applied Tokens</h4>
              <span className="text-sm text-gray-500">
                {appliedTokens.length} token{appliedTokens.length !== 1 ? 's' : ''}
              </span>
            </div>

            {tokensToShow.map((token, index) => (
              <div key={token.tokenCode} className="relative">
                <div className="flex items-center space-x-4 p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors">
                  {/* Token Icon */}
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CreditCard className="w-4 h-4 text-green-600" />
                  </div>

                  {/* Token Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-medium">{token.tokenCode}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyToken(token.tokenCode)}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Badge variant="outline" className="text-xs">
                        {token.status}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>Applied {formatDateTime(token.appliedAt)}</span>
                      {token.appliedBy && (
                        <>
                          <span>•</span>
                          <User className="w-3 h-3" />
                          <span>by @{token.appliedBy}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Token Amount */}
                  <div className="text-right">
                    <div className="font-bold text-green-600">
                      {getCurrencySymbol(token.currency)}{token.amount.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">{token.currency}</div>
                  </div>
                </div>

                {/* Connection line to next token */}
                {index < tokensToShow.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-4 bg-gray-200"></div>
                )}
              </div>
            ))}

            {/* Show More/Less Button */}
            {hasMoreTokens && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAllTokens(!showAllTokens)}
                className="w-full"
              >
                {showAllTokens ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show {sortedTokens.length - 3} More Token{sortedTokens.length - 3 !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Payment Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Tokens Applied:</span>
                <span className="font-medium">{appliedTokens.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Amount Paid:</span>
                <span className="font-medium">
                  {getCurrencySymbol(invoiceCurrency)}{totalPaid.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Invoice Amount:</span>
                <span className="font-medium">
                  {getCurrencySymbol(invoiceCurrency)}{invoiceAmount.toFixed(2)}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="font-semibold text-gray-900">
                  {remainingBalance > 0 ? 'Remaining Balance:' : 
                   remainingBalance < 0 ? 'Overpaid Amount:' : 'Status:'}
                </span>
                <span className={`font-bold ${
                  remainingBalance > 0 ? 'text-amber-600' :
                  remainingBalance < 0 ? 'text-purple-600' : 'text-green-600'
                }`}>
                  {remainingBalance === 0 ? 'Fully Paid' : 
                   `${getCurrencySymbol(invoiceCurrency)}${Math.abs(remainingBalance).toFixed(2)}`}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default InvoiceTokenBreakdown; 